import React, { useState } from 'react';
import { TopBar } from './components/TopBar';
import { BottomNavigation } from './components/BottomNavigation';
import { QuoteDetails } from './components/steps/QuoteDetails';
import { Client } from './components/steps/Client';
import { PartsSelection } from './components/steps/PartsSelection';
import { Transport } from './components/steps/Transport';
import { QuotePreview } from './components/steps/QuotePreview';
import { QuotesList } from './components/QuotesList';
import { QuotePrintView } from './components/QuotePrintView';
export interface Part {
  id: string;
  name: string;
  partNumber: string;
  quantity: number;
  unitPrice: number;
}
export interface Pivot {
  id: string;
  name: string;
  model: string;
  length: string;
  angle: string;
  area: string;
  flow: string;
  quantity: number;
}
export interface TransportRoute {
  id: string;
  from: string;
  to: string;
  type: string;
  quantity: number;
  unitPrice: number;
}
export interface QuoteData {
  parts: Part[];
  pivots: Pivot[];
  name: string;
  currency: string;
  transport: number;
  insurance: number;
  transportRoutes: TransportRoute[];
  deliveryDate: string;
  incoterm: string;
  transportInsurance: boolean;
  quoteType?: 'dealer' | 'dealer-customer' | 'final-customer';
}
export interface SavedQuote extends QuoteData {
  id: string;
  createdAt: string;
  finalizedAt?: string;
  status: 'draft' | 'finalized' | 'expired' | 'deleted';
}
export function App() {
  const [currentView, setCurrentView] = useState<'form' | 'list' | 'print'>('list');
  const [currentStep, setCurrentStep] = useState(0);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isFinalized, setIsFinalized] = useState(false);
  const [editingQuoteId, setEditingQuoteId] = useState<string | null>(null);
  const [viewingQuoteId, setViewingQuoteId] = useState<string | null>(null);
  // Initialize with sample quotes
  const [savedQuotes, setSavedQuotes] = useState<SavedQuote[]>([{
    id: 'B8TF0001',
    name: 'Pivot System - AGRO+ SRL',
    currency: 'EUR',
    parts: [{
      id: '1',
      name: 'Pivot Point Assembly',
      partNumber: 'PP-8658-STD',
      quantity: 1,
      unitPrice: 15000
    }, {
      id: '2',
      name: 'Control Panel Pro',
      partNumber: 'CP-PRO-2024',
      quantity: 1,
      unitPrice: 3500
    }, {
      id: '3',
      name: 'Span Section 6"5/8',
      partNumber: 'SS-6658-56',
      quantity: 5,
      unitPrice: 8200
    }, {
      id: '4',
      name: 'Remote Control Unit',
      partNumber: 'RCU-ADV-01',
      quantity: 2,
      unitPrice: 1200
    }],
    pivots: [{
      id: '1',
      name: 'Main Irrigation System',
      model: 'CP600',
      length: '298.81m',
      angle: '360°',
      area: '28.58 ha',
      flow: '39.12 l/s',
      quantity: 1
    }],
    transport: 850,
    insurance: 450,
    transportRoutes: [{
      id: '1',
      from: 'AISco - Albacete',
      to: 'AGRO+ SRL - Italy',
      type: 'Truck',
      quantity: 2,
      unitPrice: 425
    }],
    deliveryDate: '2025-03-15',
    incoterm: 'EXW',
    transportInsurance: true,
    quoteType: 'dealer-customer',
    createdAt: new Date('2024-12-10').toISOString(),
    finalizedAt: new Date('2024-12-12').toISOString(),
    status: 'finalized'
  }, {
    id: 'B8TF0002',
    name: 'Replacement Parts - Farm Solutions Ltd',
    currency: 'USD',
    parts: [{
      id: '1',
      name: 'Drive Motor 2HP',
      partNumber: 'DM-2HP-220V',
      quantity: 3,
      unitPrice: 850
    }, {
      id: '2',
      name: 'Pressure Regulator',
      partNumber: 'PR-75PSI-01',
      quantity: 5,
      unitPrice: 320
    }, {
      id: '3',
      name: 'Sprinkler Nozzle Set',
      partNumber: 'SN-ADJ-12',
      quantity: 24,
      unitPrice: 45
    }],
    pivots: [],
    transport: 450,
    insurance: 150,
    transportRoutes: [{
      id: '1',
      from: 'AISco - Albacete',
      to: 'Farm Solutions - USA',
      type: 'Air Freight',
      quantity: 1,
      unitPrice: 450
    }],
    deliveryDate: '2025-01-20',
    incoterm: 'CIF',
    transportInsurance: true,
    quoteType: 'final-customer',
    createdAt: new Date('2024-12-15').toISOString(),
    status: 'draft'
  }, {
    id: 'B8TF0003',
    name: 'Complete Pivot Installation - Green Fields Co',
    currency: 'EUR',
    parts: [{
      id: '1',
      name: 'Pivot Point Premium',
      partNumber: 'PP-PREM-8658',
      quantity: 2,
      unitPrice: 18500
    }, {
      id: '2',
      name: 'Advanced Control System',
      partNumber: 'ACS-2024-PRO',
      quantity: 2,
      unitPrice: 5200
    }, {
      id: '3',
      name: 'Span Section Premium',
      partNumber: 'SS-PREM-6658',
      quantity: 10,
      unitPrice: 9800
    }, {
      id: '4',
      name: 'End Gun Package',
      partNumber: 'EG-PKG-ADV',
      quantity: 2,
      unitPrice: 2800
    }, {
      id: '5',
      name: 'GPS Guidance System',
      partNumber: 'GPS-GS-2024',
      quantity: 2,
      unitPrice: 4500
    }],
    pivots: [{
      id: '1',
      name: 'North Field System',
      model: 'CP800',
      length: '420.50m',
      angle: '360°',
      area: '55.42 ha',
      flow: '68.50 l/s',
      quantity: 1
    }, {
      id: '2',
      name: 'South Field System',
      model: 'CP800',
      length: '420.50m',
      angle: '360°',
      area: '55.42 ha',
      flow: '68.50 l/s',
      quantity: 1
    }],
    transport: 2400,
    insurance: 1200,
    transportRoutes: [{
      id: '1',
      from: 'AISco - Albacete',
      to: 'Green Fields - Netherlands',
      type: 'Truck',
      quantity: 4,
      unitPrice: 600
    }],
    deliveryDate: '2024-11-30',
    incoterm: 'DDP',
    transportInsurance: true,
    quoteType: 'dealer',
    createdAt: new Date('2024-10-05').toISOString(),
    finalizedAt: new Date('2024-10-08').toISOString(),
    status: 'expired'
  }, {
    id: 'B8TF0004',
    name: 'Maintenance Kit - Valley Irrigation',
    currency: 'USD',
    parts: [{
      id: '1',
      name: 'Bearing Kit Complete',
      partNumber: 'BK-COMP-24',
      quantity: 4,
      unitPrice: 280
    }, {
      id: '2',
      name: 'Seal Kit Premium',
      partNumber: 'SK-PREM-18',
      quantity: 6,
      unitPrice: 150
    }, {
      id: '3',
      name: 'Filter Cartridge Set',
      partNumber: 'FC-SET-12',
      quantity: 12,
      unitPrice: 85
    }, {
      id: '4',
      name: 'Electrical Connector Kit',
      partNumber: 'ECK-WTPF-20',
      quantity: 3,
      unitPrice: 195
    }],
    pivots: [],
    transport: 320,
    insurance: 80,
    transportRoutes: [{
      id: '1',
      from: 'AISco - Albacete',
      to: 'Valley Irrigation - USA',
      type: 'Express Shipping',
      quantity: 1,
      unitPrice: 320
    }],
    deliveryDate: '2025-02-10',
    incoterm: 'FOB',
    transportInsurance: false,
    quoteType: 'dealer',
    createdAt: new Date('2024-12-18').toISOString(),
    status: 'draft'
  }, {
    id: 'B8TF0005',
    name: 'Urgent Repair Parts - Agritech Solutions',
    currency: 'EUR',
    parts: [{
      id: '1',
      name: 'Emergency Drive Unit',
      partNumber: 'EDU-FAST-01',
      quantity: 1,
      unitPrice: 3200
    }, {
      id: '2',
      name: 'Control Board Replacement',
      partNumber: 'CBR-2024-E',
      quantity: 1,
      unitPrice: 1850
    }, {
      id: '3',
      name: 'Safety Switch Assembly',
      partNumber: 'SSA-CERT-05',
      quantity: 2,
      unitPrice: 420
    }],
    pivots: [],
    transport: 580,
    insurance: 250,
    transportRoutes: [{
      id: '1',
      from: 'AISco - Albacete',
      to: 'Agritech - France',
      type: 'Express Truck',
      quantity: 1,
      unitPrice: 580
    }],
    deliveryDate: '2024-09-15',
    incoterm: 'EXW',
    transportInsurance: true,
    quoteType: 'final-customer',
    createdAt: new Date('2024-09-01').toISOString(),
    finalizedAt: new Date('2024-09-02').toISOString(),
    status: 'deleted'
  }, {
    id: 'B8TF0006',
    name: 'Seasonal Upgrade Package - Midwest Farms',
    currency: 'USD',
    parts: [{
      id: '1',
      name: 'Variable Rate Controller',
      partNumber: 'VRC-2024-PRO',
      quantity: 3,
      unitPrice: 2800
    }, {
      id: '2',
      name: 'Precision Nozzle Package',
      partNumber: 'PNP-VAR-48',
      quantity: 3,
      unitPrice: 1650
    }, {
      id: '3',
      name: 'Weather Station Module',
      partNumber: 'WSM-ADV-01',
      quantity: 1,
      unitPrice: 3400
    }, {
      id: '4',
      name: 'Mobile App License',
      partNumber: 'MAL-PREM-YR',
      quantity: 3,
      unitPrice: 450
    }],
    pivots: [{
      id: '1',
      name: 'Field A Upgrade',
      model: 'CP700',
      length: '350.20m',
      angle: '360°',
      area: '38.50 ha',
      flow: '52.30 l/s',
      quantity: 3
    }],
    transport: 1200,
    insurance: 600,
    transportRoutes: [{
      id: '1',
      from: 'AISco - Albacete',
      to: 'Midwest Farms - USA',
      type: 'Sea Freight',
      quantity: 2,
      unitPrice: 600
    }],
    deliveryDate: '2025-04-01',
    incoterm: 'CIF',
    transportInsurance: true,
    quoteType: 'dealer-customer',
    createdAt: new Date('2024-12-20').toISOString(),
    status: 'draft'
  }, {
    id: 'B8TF0007',
    name: 'New Installation - Organic Farms Europe',
    currency: 'EUR',
    parts: [{
      id: '1',
      name: 'Eco Pivot Point',
      partNumber: 'EPP-ECO-8658',
      quantity: 1,
      unitPrice: 16800
    }, {
      id: '2',
      name: 'Solar Panel Kit',
      partNumber: 'SPK-500W-01',
      quantity: 1,
      unitPrice: 4200
    }, {
      id: '3',
      name: 'Battery Backup System',
      partNumber: 'BBS-48V-200AH',
      quantity: 1,
      unitPrice: 3600
    }, {
      id: '4',
      name: 'Eco Span Sections',
      partNumber: 'ESS-6658-ECO',
      quantity: 6,
      unitPrice: 7800
    }, {
      id: '5',
      name: 'Smart Controller Eco',
      partNumber: 'SCE-2024-01',
      quantity: 1,
      unitPrice: 2900
    }],
    pivots: [{
      id: '1',
      name: 'Organic Field System',
      model: 'CP600-ECO',
      length: '310.40m',
      angle: '360°',
      area: '30.25 ha',
      flow: '42.80 l/s',
      quantity: 1
    }],
    transport: 950,
    insurance: 480,
    transportRoutes: [{
      id: '1',
      from: 'AISco - Albacete',
      to: 'Organic Farms - Germany',
      type: 'Eco Truck',
      quantity: 2,
      unitPrice: 475
    }],
    deliveryDate: '2025-05-15',
    incoterm: 'DAP',
    transportInsurance: true,
    quoteType: 'final-customer',
    createdAt: new Date('2024-12-22').toISOString(),
    finalizedAt: new Date('2024-12-23').toISOString(),
    status: 'finalized'
  }]);
  const [currentQuoteId, setCurrentQuoteId] = useState<string | null>(null);
  // New states for hiding discount and margin
  const [hideDiscount, setHideDiscount] = useState(false);
  const [hideMargin, setHideMargin] = useState(false);
  const [quoteData, setQuoteData] = useState<QuoteData>({
    parts: [],
    pivots: [],
    name: '',
    currency: 'USD',
    transport: 0,
    insurance: 0,
    transportRoutes: [],
    deliveryDate: '',
    incoterm: 'EXW',
    transportInsurance: false
  });
  const totalSteps = 5;
  const getQuoteStatus = () => {
    if (isFinalized) {
      return 'completed';
    }
    return 'draft';
  };
  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    }
  };
  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  const handleSaveDraft = () => {
    setIsSavingDraft(true);
    if (editingQuoteId) {
      // Si estamos editando, actualizar el quote existente
      const updatedQuote: SavedQuote = {
        ...quoteData,
        id: editingQuoteId,
        createdAt: savedQuotes.find(q => q.id === editingQuoteId)?.createdAt || new Date().toISOString(),
        status: 'draft'
      };
      setSavedQuotes(savedQuotes.map(q => q.id === editingQuoteId ? updatedQuote : q));
      setTimeout(() => {
        setIsSavingDraft(false);
        alert('Cambios guardados exitosamente');
        handleBackToList();
      }, 1000);
    } else {
      // Si es nuevo, guardar como borrador
      const newQuote: SavedQuote = {
        ...quoteData,
        id: Date.now().toString(),
        createdAt: new Date().toISOString(),
        status: 'draft'
      };
      setSavedQuotes([...savedQuotes, newQuote]);
      setTimeout(() => {
        setIsSavingDraft(false);
        alert('Cotización guardada como borrador');
      }, 1000);
    }
  };
  const handleCancelEdit = () => {
    const confirmCancel = confirm('¿Está seguro de que desea cancelar? Los cambios no guardados se perderán.');
    if (confirmCancel) {
      handleBackToList();
    }
  };
  const handleCancel = () => {
    const confirmCancel = confirm('¿Está seguro de que desea cancelar? Los cambios no guardados se perderán.');
    if (confirmCancel) {
      handleBackToList();
    }
  };
  const handleFinalize = () => {
    const newQuote: SavedQuote = {
      ...quoteData,
      id: editingQuoteId || Date.now().toString(),
      createdAt: editingQuoteId ? savedQuotes.find(q => q.id === editingQuoteId)?.createdAt || new Date().toISOString() : new Date().toISOString(),
      finalizedAt: new Date().toISOString(),
      status: 'finalized'
    };
    if (editingQuoteId) {
      setSavedQuotes(savedQuotes.map(q => q.id === editingQuoteId ? newQuote : q));
    } else {
      setSavedQuotes([...savedQuotes, newQuote]);
    }
    setIsFinalized(true);
    alert('Cotización finalizada y guardada exitosamente');
    setTimeout(() => {
      handleBackToList();
    }, 1500);
  };
  const handleNewQuote = () => {
    const newQuoteId = `B8TF${Math.floor(Math.random() * 10000).toString().padStart(4, '0')}`;
    setCurrentQuoteId(newQuoteId);
    setQuoteData({
      parts: [],
      pivots: [],
      name: '',
      currency: 'USD',
      transport: 0,
      insurance: 0,
      transportRoutes: [],
      deliveryDate: '',
      incoterm: 'EXW',
      transportInsurance: false
    });
    setCurrentStep(0);
    setIsFinalized(false);
    setEditingQuoteId(null);
    setCurrentView('form');
  };
  const handleEditQuote = (quoteId: string) => {
    const quote = savedQuotes.find(q => q.id === quoteId);
    if (quote) {
      setQuoteData({
        parts: quote.parts,
        pivots: quote.pivots,
        name: quote.name,
        currency: quote.currency,
        transport: quote.transport,
        insurance: quote.insurance,
        transportRoutes: quote.transportRoutes,
        deliveryDate: quote.deliveryDate,
        incoterm: quote.incoterm,
        transportInsurance: quote.transportInsurance
      });
      setEditingQuoteId(quoteId);
      setCurrentQuoteId(quoteId);
      setIsFinalized(false);
      setCurrentStep(0);
      setCurrentView('form');
    }
  };
  const handleViewQuote = (quoteId: string) => {
    setViewingQuoteId(quoteId);
    setCurrentView('print');
  };
  const handleDeleteQuote = (quoteId: string) => {
    if (confirm('¿Está seguro de que desea eliminar esta cotización?')) {
      setSavedQuotes(savedQuotes.filter(q => q.id !== quoteId));
    }
  };
  const handleBackToList = () => {
    setCurrentView('list');
    setViewingQuoteId(null);
    setEditingQuoteId(null);
    setIsFinalized(false);
  };
  const handleAddExtras = () => {
    console.log('Agregar extras');
    alert('Función de agregar extras - Próximamente');
  };
  const handlePrint = () => {
    console.log('Imprimir cotización');
    window.print();
  };
  const handleDownload = () => {
    console.log('Descargar cotización');
    alert('Función de descarga - Próximamente');
  };
  const canProceed = () => {
    return true;
  };
  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return <QuoteDetails quoteData={quoteData} setQuoteData={setQuoteData} data-id="element-0" />;
      case 1:
        return <Client quoteData={quoteData} setQuoteData={setQuoteData} data-id="element-1" />;
      case 2:
        return <Transport quoteData={quoteData} setQuoteData={setQuoteData} data-id="element-2" />;
      case 3:
        return <PartsSelection quoteData={quoteData} setQuoteData={setQuoteData} data-id="element-3" />;
      case 4:
        return <QuotePreview quoteData={quoteData} setQuoteData={setQuoteData} data-id="element-4" />;
      default:
        return null;
    }
  };
  if (currentView === 'list') {
    return <div className="w-full min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-gray-100" data-id="element-5">
        <QuotesList quotes={savedQuotes} onNewQuote={handleNewQuote} onEditQuote={handleEditQuote} onViewQuote={handleViewQuote} onDeleteQuote={handleDeleteQuote} data-id="element-6" />
      </div>;
  }
  if (currentView === 'print' && viewingQuoteId) {
    const quote = savedQuotes.find(q => q.id === viewingQuoteId);
    if (quote) {
      return <QuotePrintView quote={quote} onBack={handleBackToList} onDownload={handleDownload} onEdit={() => handleEditQuote(viewingQuoteId)} onDelete={() => {
        handleDeleteQuote(viewingQuoteId);
        handleBackToList();
      }} data-id="element-7" />;
    }
  }
  return <div className="w-full min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-gray-100 flex flex-col" data-id="element-8">
      <TopBar quoteName={quoteData.name} isFinalized={isFinalized} onAddExtras={handleAddExtras} onPrint={handlePrint} onDownload={handleDownload} currentStep={currentStep} totalSteps={totalSteps} designVariant="deep-ocean-blue" isEditMode={editingQuoteId !== null} onCancelEdit={handleCancelEdit} onCancel={handleCancel} onSaveDraft={handleSaveDraft} hideDiscount={hideDiscount} hideMargin={hideMargin} onToggleHideDiscount={() => setHideDiscount(!hideDiscount)} onToggleHideMargin={() => setHideMargin(!hideMargin)} quoteType={quoteData.quoteType} quoteId={currentQuoteId || undefined} lastUpdated={editingQuoteId ? new Date(savedQuotes.find(q => q.id === editingQuoteId)?.createdAt || Date.now()).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }) : undefined} data-id="element-9" />
      <div className="flex-1 flex items-start justify-center p-8 pb-48 overflow-y-auto" data-id="element-10">
        <div className="w-full max-w-7xl" data-id="element-11">{renderStep()}</div>
      </div>
      <BottomNavigation currentStep={currentStep} totalSteps={totalSteps} onBack={handleBack} onNext={handleNext} onSaveDraft={handleSaveDraft} onFinalize={handleFinalize} canProceed={canProceed()} quoteStatus={getQuoteStatus()} isSavingDraft={isSavingDraft} isEditMode={editingQuoteId !== null} designVariant="deep-ocean-blue" onCancel={handleCancel} data-id="element-12" />
    </div>;
}